<script setup lang="ts">
import { Fancybox } from "@fancyapps/ui";
import { find } from "lodash";
import { onBeforeUnmount, onMounted, ref } from "vue";
import type { ProductImageResponse } from "../../../responses/ProductImageResponse";
import type { ProductResponse } from "../../../responses/ProductResponse";
import type { ProductVariantResponse } from "../../../responses/ProductVariantResponse";
import { useProductStore } from "../../stores/product";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import NoImageURL from "../../../../images/no-image.png";
import VideoWrapper from "../VideoWrapper.vue";

interface Props {
    product: ProductResponse;
}

const props = defineProps<Props>();
const selectedImage = ref(props.product.images[0]);
const productStore = useProductStore();

Fancybox.bind("[data-fancybox]", {
    Thumbs: false,
    videoAutoplay: false,
});

const handleSelectedVariantChanged = async (
    variant: ProductVariantResponse,
) => {
    if (!variant) {
        return;
    }

    const variantImage = find(
        props.product.images,
        (productImage: ProductImageResponse) =>
            productImage.product_variant_id === variant.id,
    );

    selectedImage.value = variantImage ?? props.product.images[0];
};

onMounted(() => {
    productStore.events.on(
        "selectedVariantChanged",
        handleSelectedVariantChanged,
    );
});

onBeforeUnmount(() => {
    productStore.events.off(
        "selectedVariantChanged",
        handleSelectedVariantChanged,
    );
});
</script>
<template>
    <div class="flex" v-if="product.images.length">
        <div class="flex flex-col items-start justify-start gap-1 pr-1 overflow-y-auto w-2/12">
            <div class="p-1 border w-fit h-fit cursor-pointer border-secondary"
                 :class="{'border-black': image.id === selectedImage.id}" v-for="image in product.images">
                <div class="w-full">
                    <img :alt="image.alt_text ?? product.name" :src="image.image_url" @click="selectedImage = image;"/>
                </div>
            </div>
        </div>
        <div class="p-1 bg-secondary-content rounded-xl relative w-10/12">
            <a :href="image.video_id !== null ? image.video_embed_url : image.image_url"
               :data-type="image.video_id !== null ? 'iframe' : 'image'" v-for="image in product.images"
               :class="{'hidden': image.id !== selectedImage.id}" data-fancybox="gallery">
                <video-wrapper v-if="selectedImage?.video_id !== null" class="w-full lg:block"
                               :video-id="selectedImage.video_id" :source="selectedImage.video_service"></video-wrapper>
                <img v-else :alt="selectedImage.alt_text ?? product.name" :src="selectedImage.image_url"/>
                <div class="flex flex-row absolute top-7 right-7 gap-2">
                    <span class="text-sm leading-5 px-3 py-1 font-semibold bg-primary text-white rounded-full"
                          v-for="label in product.product_labels" v-text="label.label"></span>
                </div>
            </a>
        </div>
    </div>
    <div v-else>
        <div class="overflow-hidden relative w-fit">
            <img :alt="product.name" :src="product.thumbnail?.image_url ?? NoImageURL" data-fancybox="gallery"/>
        </div>
    </div>
</template>
